import { biToast } from '@pulsepointinc/bi-components';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LONG_STALE_TIME, queryClient } from '../../lib';
import { workspaceTypeChecks } from '../../lib/api/helpers';
import {
  type CreateWorkspaceRequest,
  createNewWorkspace,
  type DeleteWorkspaceRequest,
  type DeleteWorkspaceResponse,
  type DuplicateWorkspaceRequest,
  type DuplicateWorkspaceResponse,
  deleteWorkspace,
  duplicateWorkspace,
  type ExpansionWorkspaceMetaDataRequest,
  expansionMetaData,
  type GetDependenciesRequest,
  type GetDependenciesResponse,
  type GetSessionWorkSpaceRequest,
  type GetSessionWorkSpaceResponse,
  type GetWorkspaceContextRequest,
  type GetWorkspaceNameCheckRequest,
  type GetWorkspaceNameCheckResponse,
  type GetWorkspaceRequest,
  type GetWorkspacesRequest,
  getDependencies,
  getSessionWorkspace,
  getSingleWorkspace,
  getUsersWorkspaces,
  getWorkspaceContext,
  getWorkspaceNameValidation,
  getWorkspaceTypes,
  type RenameWorkspaceRequest,
  renameWorkspace,
  type SaveWorkspaceRequest,
  type SaveWorkspaceResponse,
  saveWorkspace,
  type WorkspacePayload,
} from '../../lib/api/workspaces';
import { API_TAGS } from '../../lib/constants';
import { useCurrentWorkspace } from '../useCurrentWorkspace';

export function useCreateNewWorkspaceMutation() {
  const mutation = useMutation<WorkspacePayload, unknown, CreateWorkspaceRequest>({
    mutationFn: createNewWorkspace,
    mutationKey: ['createNewWorkspace'],
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.Workspaces],
      });

      queryClient.setQueryData(
        [
          API_TAGS.SingleWorkspace,
          {
            accountID: variables.accountID,
            workspaceID: data.workspace_id,
            // TODO: Remove workspaceTypeID when all types are migrated to V7
            workspaceTypeID: workspaceTypeChecks.isV7Workspace(variables.typeID) ? variables.typeID : undefined,
          },
        ],
        {
          ...data,
          workspace_looker_dashboard_details: {},
        },
      );
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useDuplicateWorkspaceMutation() {
  const mutation = useMutation<DuplicateWorkspaceResponse, unknown, DuplicateWorkspaceRequest>({
    mutationFn: ({ workspaceTypeId, ...body }) => duplicateWorkspace(body, workspaceTypeId),
    mutationKey: ['duplicateWorkspace'],
    onError: () => {
      biToast.error('Duplication Failed', 'Unable to duplicate workspace. Please try again.');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.Workspaces],
      });
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useSaveWorkspaceMutation() {
  const { currentWorkspace } = useCurrentWorkspace();

  const mutation = useMutation<SaveWorkspaceResponse, unknown, SaveWorkspaceRequest>({
    mutationFn: (body) => saveWorkspace(body, currentWorkspace?.workspace_type_id),
    mutationKey: [API_TAGS.SaveWorkspace],
    onError: () => {
      biToast.error('Workspace saving error. Try again.');
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: [API_TAGS.Workspaces],
      });
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.SingleWorkspace],
      });
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useRenameWorkspaceMutation() {
  const mutation = useMutation<unknown, unknown, RenameWorkspaceRequest>({
    mutationFn: ({ workspaceTypeId, ...body }) => renameWorkspace(body, workspaceTypeId),
    mutationKey: ['renameWorkspace'],
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.Workspaces],
      });
      queryClient.setQueriesData<WorkspacePayload>({ queryKey: [API_TAGS.SingleWorkspace] }, (old) =>
        old ? { ...old, workspace_name: variables.workspaceName } : old,
      );
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useDeleteWorkspaceMutation() {
  const mutation = useMutation<DeleteWorkspaceResponse, unknown, DeleteWorkspaceRequest>({
    mutationFn: deleteWorkspace,
    mutationKey: ['deleteWorkspace'],
    onError: () => {
      biToast.error('Delete Failed', 'Unable to delete workspace. Please try again.');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [API_TAGS.Workspaces],
      });
    },
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useGetDependenciesMutation() {
  const mutation = useMutation<GetDependenciesResponse, unknown, GetDependenciesRequest>({
    mutationFn: getDependencies,
    mutationKey: ['getDependencies'],
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useGetUsersWorkspacesQuery(
  params: GetWorkspacesRequest,
  options?: { enabled?: boolean; staleTime?: number },
) {
  const { advertiserMapping: _advertiserMapping, ...cacheKeyParams } = params;

  return useQuery({
    queryKey: [API_TAGS.Workspaces, cacheKeyParams],
    queryFn: () => getUsersWorkspaces(params),
    meta: {
      onError: () => {
        biToast.error('Failed to Load', 'Unable to load workspaces. Please refresh the page.');
      },
    },
    enabled: options?.enabled ?? true,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
  });
}

export function useGetUsersSingleWorkspaceQuery(
  params: GetWorkspaceRequest,
  options?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery({
    queryKey: [API_TAGS.SingleWorkspace, params],
    queryFn: () => getSingleWorkspace(params),
    meta: {
      onError: () => {
        biToast.error('Failed to Load', 'Unable to load workspaces. Please refresh the page.');
      },
    },
    enabled: options?.enabled ?? true,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
  });
}

export function useGetWorkspaceContextQuery(
  params: GetWorkspaceContextRequest,
  options?: { enabled?: boolean; staleTime?: number },
) {
  return useQuery({
    queryKey: [API_TAGS.WorkspaceContext, params],
    queryFn: () => getWorkspaceContext(params),
    enabled: options?.enabled ?? true,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
  });
}

export function useGetWorkspaceTypesQuery(options?: { enabled?: boolean; staleTime?: number }) {
  return useQuery({
    queryKey: [API_TAGS.WorkspaceTypes],
    queryFn: getWorkspaceTypes,
    meta: {
      onError: () => {
        biToast.error('Failed to Load', 'Unable to load workspace types. Please refresh the page.');
      },
    },
    enabled: options?.enabled ?? true,
    ...(options?.staleTime ? { staleTime: options.staleTime } : {}),
  });
}

export function useLazyGetSessionWorkspaceQuery() {
  const mutation = useMutation<GetSessionWorkSpaceResponse, unknown, GetSessionWorkSpaceRequest>({
    mutationFn: getSessionWorkspace,
    mutationKey: ['getSessionWorkspace'],
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}

export function useExpansionMetaDataQuery(params: ExpansionWorkspaceMetaDataRequest, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [API_TAGS.ExpansionMetaData, params],
    queryFn: () => expansionMetaData(params),
    meta: {
      onError: () => {
        biToast.error('Failed to Load', 'Unable to load expansion metadata. Please refresh the page.');
      },
    },
    enabled: options?.enabled ?? true,
    staleTime: LONG_STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false, // When the API fails we don't want to keep retrying as it clogs the connection pool.
  });
}

export function useLazyGetWorkspaceNameValidationQuery() {
  const mutation = useMutation<GetWorkspaceNameCheckResponse, unknown, GetWorkspaceNameCheckRequest>({
    mutationFn: ({ workspaceTypeId, ...params }) => getWorkspaceNameValidation(params, workspaceTypeId),
    mutationKey: ['getWorkspaceNameValidation'],
  });

  return {
    ...mutation,
    trigger: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isFetching: mutation.isPending,
  };
}
